const MARK_RE = /^\p{M}$/u;
const REGIONAL_INDICATOR_RE = /^\p{Regional_Indicator}$/u;
const VARIATION_SELECTOR_RE = /^[\u{fe00}-\u{fe0f}\u{e0100}-\u{e01ef}]$/u;
const EMOJI_MODIFIER_RE = /^\p{Emoji_Modifier}$/u;
const JOINER_RE = /^[\u200c\u200d]$/u;

/**
 * Viramas make the following consonant part of the same grapheme. The fallback
 * only runs in browsers without `Intl.Segmenter`; this set covers the viramas
 * used by the Indic and Southeast Asian scripts published by the site.
 */
const VIRAMAS = new Set([
  "\u094d",
  "\u09cd",
  "\u0a4d",
  "\u0acd",
  "\u0b4d",
  "\u0bcd",
  "\u0c4d",
  "\u0ccd",
  "\u0d3b",
  "\u0d3c",
  "\u0d4d",
  "\u0dca",
  "\u1039",
  "\u1714",
  "\u1734",
  "\u17d2",
  "\ua806",
  "\ua8c4",
  "\ua953",
  "\ua9c0",
  "\uaaf6",
  "\uabed",
]);

function isExtension(character: string): boolean {
  return (
    MARK_RE.test(character) ||
    VARIATION_SELECTOR_RE.test(character) ||
    EMOJI_MODIFIER_RE.test(character)
  );
}

/**
 * A small deterministic fallback for environments without `Intl.Segmenter`.
 *
 * It keeps combining marks with their base letter, Indic conjuncts joined by a
 * virama, emoji modifiers and variation selectors, ZWJ sequences, and regional
 * indicator pairs together. The native segmenter remains authoritative when
 * available.
 */
export function segmentGraphemesFallback(value: string): string[] {
  const clusters: string[] = [];
  let current = "";
  let joinsNext = false;
  let regionalIndicators = 0;

  for (const character of value.normalize("NFC")) {
    if (current.length === 0) {
      current = character;
      regionalIndicators = REGIONAL_INDICATOR_RE.test(character) ? 1 : 0;
      continue;
    }

    if (JOINER_RE.test(character)) {
      current += character;
      joinsNext = true;
      continue;
    }

    if (joinsNext) {
      current += character;
      joinsNext = false;
      regionalIndicators = 0;
      continue;
    }

    if (isExtension(character)) {
      current += character;
      joinsNext = VIRAMAS.has(character);
      continue;
    }

    const isRegionalIndicator = REGIONAL_INDICATOR_RE.test(character);
    if (isRegionalIndicator && regionalIndicators % 2 === 1) {
      current += character;
      regionalIndicators += 1;
      continue;
    }

    clusters.push(current);
    current = character;
    regionalIndicators = isRegionalIndicator ? 1 : 0;
  }

  if (current.length > 0) {
    clusters.push(current);
  }
  return clusters;
}

/** Segment text into user-perceived characters, normalized for comparison. */
export function segmentGraphemes(value: string, locale?: string): string[] {
  const normalized = value.normalize("NFC");
  if (typeof Intl.Segmenter !== "function") {
    return segmentGraphemesFallback(normalized);
  }

  try {
    const segmenter = new Intl.Segmenter(locale, { granularity: "grapheme" });
    return Array.from(segmenter.segment(normalized), ({ segment }) => segment);
  } catch {
    return segmentGraphemesFallback(normalized);
  }
}

export function graphemeLength(value: string, locale?: string): number {
  return segmentGraphemes(value, locale).length;
}
